/**
 * THE EIGHT SWITCHES `g_set_mod.dc.html` DRAWS, and what each one really
 * reaches in this product today.
 *
 * The design's module set and the EMR's are not the same set. `ModuleKey`
 * (src/types/featureToggle.ts) gates PATIENT_MANAGEMENT … VOICE_AI — the
 * clinical spine — and the board's eight are the OPERATIONAL surfaces a clinic
 * switches off when it tried one and stopped.
 *
 * `built` is not decoration: an unbuilt module's switch is rendered off and
 * refused, because storing `stock: false` when no stock module exists would
 * hide nothing and say it had (BRIEF §4.8). Three of these flipped to true
 * when their lanes landed — Stock, the Technician worklist, and Expenses &
 * payroll — and their copy had to change with them: a switch that still said
 * "no stock module yet" over a built one is the same lie in the other
 * direction.
 *
 * `key` is what goes into `system_settings['modules.enabled']`, the same map
 * the EMR already reads on boot.
 */
export interface ModuleDef {
  key: string;
  title: string;
  /** The board's caption when the module carries no live figure. */
  sub: string;
  /** Which live figure this row's caption is, if any. */
  live?: 'nurses' | 'technicians' | 'providers' | 'stock';
  /** False while nothing in the product is gated on this key. */
  built: boolean;
  /** What turning it off would actually do — the pane's first card. */
  off: string;
  /** Who or what it would leave behind, and in what state. */
  keeps: string;
}

export const MODULES: ModuleDef[] = [
  {
    key: 'stock', title: 'Stock & inventory', sub: '', live: 'stock',
    built: true,
    off: 'The shelf, the catalog, receiving, orders, counts and transfers all stop being drawn — for the stock manager and for the nurse who only reads it. Every item, lot and movement is kept exactly as it stands.',
    keeps: 'Stock managers keep their sign-in, and the movements behind today’s numbers stay readable the moment it is switched back on — a module is not a role, and turning one off is not a deletion.',
  },
  {
    key: 'nurseTasks', title: 'Nurse tasks', sub: '', live: 'nurses',
    built: true,
    off: 'The nurse home and its task list stop being drawn for everyone. Tasks already raised are kept and reappear the moment it is switched back on.',
    keeps: 'Nurses keep their sign-in and every other screen their role reaches — a module is not a role.',
  },
  {
    key: 'technicianWorklist', title: 'Technician worklist', sub: '', live: 'technicians',
    built: true,
    off: 'The worklist stops being drawn, and imaging and lab work in progress goes back to being tracked on the order itself.',
    keeps: 'Technicians keep their sign-in and every task already raised; nothing in flight is lost.',
  },
  {
    key: 'expensesPayroll', title: 'Expenses & payroll', sub: 'used by the accountant',
    built: true,
    off: 'Expenses, payables and the payout run stop being drawn. Bills and claims are untouched — they are the product, not a module.',
    keeps: 'Every expense, recurring line and payout run is kept, including one sent for approval: turning the screen off does not un-approve anybody’s pay.',
  },
  {
    key: 'directory', title: 'Directory', sub: '', live: 'providers',
    built: true,
    off: 'The registry of labs, imaging centres, suppliers and referral doctors stops being drawn, and nothing new can be routed to an external provider.',
    keeps: 'Every provider, every past referral and every result already received is kept exactly as it stands.',
  },
  {
    key: 'patientPortal', title: 'Patient portal & messages', sub: 'and the message wallet',
    built: true,
    off: 'The portal stops accepting magic links and no outgoing patient message is sent — which also means the wallet stops being spent.',
    keeps: 'The wallet balance is money, not credits, so it stays exactly where it is and never expires.',
  },
  {
    key: 'reports', title: 'Reports & analytics', sub: 'every door behind Reports',
    built: true,
    off: 'Every door behind Reports closes — revenue, visits, sources, insurance, edits, charts and trends — and the Reports door itself stops leading anywhere.',
    keeps: 'Nothing is deleted: a report is computed from the bills and visits each time it is opened.',
  },
  {
    key: 'flows', title: 'Flows', sub: 'team choreography',
    built: true,
    off: 'The Flows editor and the step lists it draws stop being shown. Every saved flow is kept exactly as it stands.',
    keeps: 'Nothing a flow already recorded is lost, and switching Flows back on restores every saved flow.',
  },
];

/** The two that are the product and can never be turned off. */
export const MODULE_FLOOR =
  'Patients, the encounter and billing cannot be turned off — they are the product.';
